let ac = null;
let master = null;
let reverb = null;
let voiceOut = null; // per-hit output bus (used for stereo panning)

function makeImpulse(context, dur, decay) {
  const rate = context.sampleRate;
  const len = Math.floor(rate * dur);
  const buf = context.createBuffer(2, len, rate);
  for (let ch = 0; ch < 2; ch++) {
    const d = buf.getChannelData(ch);
    for (let i = 0; i < len; i++) {
      d[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / len, decay);
    }
  }
  return buf;
}

function getCtx() {
  if (!ac) {
    ac = new (window.AudioContext || window.webkitAudioContext)();
    master = ac.createGain();
    master.gain.value = 1.25; // louder overall so hits are clearly heard
    // gentle limiter to keep loud hits from clipping
    const comp = ac.createDynamicsCompressor();
    comp.threshold.value = -10;
    comp.knee.value = 24;
    comp.ratio.value = 4;
    comp.attack.value = 0.003;
    comp.release.value = 0.25;
    master.connect(comp).connect(ac.destination);

    const conv = ac.createConvolver();
    conv.buffer = makeImpulse(ac, 1.6, 3.0);
    const rev = ac.createGain();
    rev.gain.value = 0.18;
    conv.connect(rev).connect(comp);
    reverb = conv;
  }
  if (ac.state === "suspended") ac.resume();
  return ac;
}

const rand = (a, b) => a + Math.random() * (b - a);

function membrane(context, t0, { freq, to, type = "sine", dur, gain, reverbSend = 0.25 }) {
  const osc = context.createOscillator();
  const g = context.createGain();
  osc.type = type;
  osc.frequency.setValueAtTime(freq, t0);
  osc.frequency.exponentialRampToValueAtTime(Math.max(20, to), t0 + dur);
  g.gain.setValueAtTime(0.0001, t0);
  g.gain.exponentialRampToValueAtTime(gain, t0 + 0.005);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(g).connect(voiceOut || master);
  const send = context.createGain();
  send.gain.value = reverbSend;
  g.connect(send).connect(reverb);
  osc.start(t0);
  osc.stop(t0 + dur + 0.05);
}

function noise(context, t0, { dur, gain, type = "bandpass", freq, q = 1, reverbSend = 0.2 }) {
  const src = context.createBufferSource();
  const len = Math.floor(context.sampleRate * dur);
  const buf = context.createBuffer(1, len, context.sampleRate);
  const data = buf.getChannelData(0);
  for (let i = 0; i < len; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / len);
  src.buffer = buf;
  const filter = context.createBiquadFilter();
  filter.type = type;
  filter.frequency.value = freq;
  filter.Q.value = q;
  const g = context.createGain();
  g.gain.setValueAtTime(gain, t0);
  g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  src.connect(filter).connect(g).connect(voiceOut || master);
  const send = context.createGain();
  send.gain.value = reverbSend;
  g.connect(send).connect(reverb);
  src.start(t0);
  src.stop(t0 + dur + 0.02);
}

// resume/unlock the audio context (call on first user gesture)
export const unlockAudio = () => {
  getCtx();
};

// ---- Ambient drone: a soft, low background pad so silences feel calmer ----
let ambient = null;

export const startAmbient = () => {
  const c = getCtx();
  if (ambient) return;
  const out = c.createGain();
  out.gain.value = 0.0001;
  const lp = c.createBiquadFilter();
  lp.type = "lowpass";
  lp.frequency.value = 420;
  lp.Q.value = 0.6;
  out.connect(lp).connect(master);

  // send a little to the reverb for space
  const send = c.createGain();
  send.gain.value = 0.5;
  out.connect(send).connect(reverb);

  // low root + fifth + octave, gently detuned
  const freqs = [55, 82.4, 110];
  const oscs = freqs.map((f, i) => {
    const o = c.createOscillator();
    o.type = "sine";
    o.frequency.value = f;
    o.detune.value = (i - 1) * 4;
    o.connect(out);
    o.start();
    return o;
  });

  // slow "breathing" LFO on the drone's gain
  const lfo = c.createOscillator();
  lfo.type = "sine";
  lfo.frequency.value = 0.08; // ~12.5s cycle
  const lfoGain = c.createGain();
  lfoGain.gain.value = 0.012;
  lfo.connect(lfoGain).connect(out.gain);
  lfo.start();

  const now = c.currentTime;
  out.gain.setValueAtTime(0.0001, now);
  out.gain.exponentialRampToValueAtTime(0.04, now + 3);

  ambient = { out, oscs, lfo };
};

export const stopAmbient = () => {
  if (!ambient) return;
  const c = getCtx();
  const now = c.currentTime;
  const a = ambient;
  ambient = null;
  try {
    a.out.gain.cancelScheduledValues(now);
    a.out.gain.setValueAtTime(Math.max(0.0002, a.out.gain.value), now);
    a.out.gain.exponentialRampToValueAtTime(0.0001, now + 1.2);
    a.oscs.forEach((o) => o.stop(now + 1.4));
    a.lfo.stop(now + 1.4);
  } catch (e) {}
};

export const isAmbientOn = () => !!ambient;

// playDrum(name, { pitch, velocity })
// pitch: multiplier for the drum's fundamental (musical "voice" variation)
// velocity: 0..1 loudness scaling per hit
export const playDrum = (name, opts = {}) => {
  const c = getCtx();
  const t = c.currentTime;
  const d = rand(0.97, 1.04); // subtle per-hit detune so no two hits feel identical
  const p = (opts.pitch ?? 1) * d;
  const v = Math.max(0.4, Math.min(1.15, opts.velocity ?? 1));

  // spatial: route this hit through a stereo panner placed by on-screen position
  let panner = null;
  if (typeof opts.pan === "number" && c.createStereoPanner) {
    panner = c.createStereoPanner();
    panner.pan.setValueAtTime(Math.max(-1, Math.min(1, opts.pan)), t);
    panner.connect(master);
    voiceOut = panner;
  }

  switch (name) {
    case "djembe": // RED — deep, round, warm bass tone with a full skin attack
      membrane(c, t, { freq: 180 * p, to: 62, type: "sine", dur: 0.6, gain: 1.15 * v, reverbSend: 0.32 });
      membrane(c, t, { freq: 92 * p, to: 50, type: "triangle", dur: 0.4, gain: 0.42 * v, reverbSend: 0.22 });
      noise(c, t, { dur: 0.1, gain: 0.4 * v, type: "bandpass", freq: 230, q: 0.8 });
      break;
    case "conga": // YELLOW — bright, tight, woody
      membrane(c, t, { freq: 300 * p, to: 158, type: "sine", dur: 0.3, gain: 1.0 * v, reverbSend: 0.26 });
      membrane(c, t, { freq: 200 * p, to: 125, type: "triangle", dur: 0.22, gain: 0.34 * v, reverbSend: 0.18 });
      noise(c, t, { dur: 0.055, gain: 0.32 * v, type: "bandpass", freq: 560, q: 1.2 });
      break;
    case "tom": // BLUE — low, resonant, long sustain
      membrane(c, t, { freq: 155 * p, to: 55, type: "sine", dur: 0.78, gain: 1.1 * v, reverbSend: 0.42 });
      membrane(c, t, { freq: 232 * p, to: 92, type: "triangle", dur: 0.34, gain: 0.28 * v, reverbSend: 0.26 });
      break;
    case "slap": // GREEN — sharp, bright crack
      noise(c, t, { dur: 0.16, gain: 0.72 * v, type: "highpass", freq: 1900, q: 0.7, reverbSend: 0.3 });
      noise(c, t, { dur: 0.07, gain: 0.5 * v, type: "bandpass", freq: 1550, q: 1.5 });
      membrane(c, t, { freq: 540 * p, to: 250, type: "triangle", dur: 0.09, gain: 0.3 * v, reverbSend: 0.12 });
      break;
    case "clap": // MAUVE — hand clap: a few quick noise bursts + a short room tail
      [0, 0.011, 0.022].forEach((off) => {
        noise(c, t + off, { dur: 0.04, gain: 0.55 * v, type: "bandpass", freq: 1250, q: 1.1, reverbSend: 0.16 });
      });
      noise(c, t + 0.03, { dur: 0.19, gain: 0.42 * v, type: "bandpass", freq: 1000, q: 0.7, reverbSend: 0.3 });
      break;
    case "shaker": // TEAL — crisp shaker "shik"
      noise(c, t, { dur: 0.06, gain: 0.4 * v, type: "highpass", freq: 6500, q: 0.7, reverbSend: 0.1 });
      noise(c, t + 0.004, { dur: 0.03, gain: 0.24 * v, type: "bandpass", freq: 9200, q: 0.9 });
      break;
    default:
      membrane(c, t, { freq: 200 * p, to: 90, type: "sine", dur: 0.3, gain: 0.7 * v });
  }
  voiceOut = null;
};
